/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {EmailAddress} from 'spine-web/proto/spine/net/email_address_pb';
import {
  // @ts-expect-error
  ProductId, CommercialLicenseViewId, SingleDevLicenseId, ProjectLicenseId, CompanyWideLicenseId,
  // @ts-expect-error
  PersonalLicenseId, SingleDevSourceCodeLicenseId, ProjectSourceCodeLicenseId,
  // @ts-expect-error
  CompanyWideSourceCodeLicenseId
} from '@proto/teamdev/licensing/identifiers_pb';

/**
 * The ID of a product version.
 */
export class ProductVersionId {
  value: string;
  productId: ProductId;

  constructor(params: {value: string; productId: ProductId}) {
    this.value = params.value;
    this.productId = params.productId;
  }
}

/**
 * The ID of an evaluation.
 */
export class EvaluationId {
  uuid: string;
  email?: EmailAddress;
  productId?: ProductId;

  constructor(params: {uuid: string; email?: EmailAddress; productId?: ProductId}) {
    this.uuid = params.uuid;
    this.email = params.email;
    this.productId = params.productId;
  }
}

// ========================== Commercial license ID ==========================

export function getOneOfCommercialLicenseIds(id: CommercialLicenseViewId)
    : OneOfCommercialLicenseIds {
  const getters = {
    [CommercialLicenseViewId.IdCase.SINGLE_DEV_LICENSE_ID]:               () => id.getSingleDevLicenseId(),
    [CommercialLicenseViewId.IdCase.PROJECT_LICENSE_ID]:                  () => id.getProjectLicenseId(),
    [CommercialLicenseViewId.IdCase.COMPANY_WIDE_LICENSE_ID]:             () => id.getCompanyWideLicenseId(),
    [CommercialLicenseViewId.IdCase.PERSONAL_LICENSE_ID]:                 () => id.getPersonalLicenseId(),
    [CommercialLicenseViewId.IdCase.SINGLE_DEV_SOURCE_CODE_LICENSE_ID]:   () => id.getSingleDevSourceCodeLicenseId(),
    [CommercialLicenseViewId.IdCase.PROJECT_SOURCE_CODE_LICENSE_ID]:      () => id.getProjectSourceCodeLicenseId(),
    [CommercialLicenseViewId.IdCase.COMPANY_WIDE_SOURCE_CODE_LICENSE_ID]: () => id.getCompanyWideSourceCodeLicenseId(),
  };
  const whichOne = id.getIdCase();
  const get = getters[whichOne];
  if (!get) {
    throw new Error(`Cannot retrieve a commercial license ID of type '${whichOne}'.`);
  }
  return get();
}

// ========================== Specific commercial license IDs ==========================

/**
 * A base class for different types of `CommercialLicenseViewId` values.
 */
type OneOfCommercialLicenseIds = SingleDevLicenseId | ProjectLicenseId | CompanyWideLicenseId |
                                 PersonalLicenseId | SingleDevSourceCodeLicenseId |
                                 ProjectSourceCodeLicenseId | CompanyWideSourceCodeLicenseId;

/**
 * An ID for a non-commercial license request projection.
 */
export class NonCommercialLicenseRequestId {
  academicId?: AcademicLicenseId;
  openSourceId?: OpenSourceLicenseId;

  /* TODO:2022-01-17:dmitry.pikhulya [Issue #346]:
       Why exactly do we need so complex IDs in view entities? */

  constructor(params: {
    academicId?: AcademicLicenseId;
    openSourceId?: OpenSourceLicenseId;
  }) {
    this.academicId = params.academicId;
    this.openSourceId = params.openSourceId;
  }
}

/**
 * An ID for a non-commercial license projection.
 */
export class NonCommercialLicenseId {
  id_academicId?: AcademicLicenseId;
  id_openSourceId?: OpenSourceLicenseId;

  constructor(params: {
    id_academicId?: AcademicLicenseId;
    id_openSourceId?: OpenSourceLicenseId;
  }) {
    this.id_academicId = params.id_academicId;
    this.id_openSourceId = params.id_openSourceId;
  }
}

/**
 * The ID of an academic license.
 */
export class AcademicLicenseId {
  uuid: string;
  email?: EmailAddress;
  productId?: ProductId;

  constructor(params: {
    uuid: string;
    email?: EmailAddress;
    productId?: ProductId;
  }) {
    this.uuid = params.uuid;
    this.email = params.email;
    this.productId = params.productId;
  }
}

/**
 * The ID of an open-source license.
 */
export class OpenSourceLicenseId {
  uuid: string;
  email?: EmailAddress;
  productId?: ProductId;

  constructor(params: {
    uuid: string;
    email?: EmailAddress;
    productId?: ProductId;
  }) {
    this.uuid = params.uuid;
    this.email = params.email;
    this.productId = params.productId;
  }
}

/**
 * The ID of a demo license.
 */
export class DemoLicenseId {
  uuid: string;
  email?: EmailAddress;
  productId?: ProductId;

  constructor(params: {
    uuid: string;
    email?: EmailAddress;
    productId?: ProductId;
  }) {
    this.uuid = params.uuid;
    this.email = params.email;
    this.productId = params.productId;
  }
}
